import { useState, useEffect, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useGetVorQuery } from '../../api/endpoints/technicalSupportRequest';
import { useGetLookUpDropdownListQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';
import GenericFileExportButton from '../../components/common/GenericFileExportButton';
import { useExportDailyInactiveVehicleToExcelMutation } from '../../api/endpoints/technicalSupportRequest';

const STATUS_DAY_FIELDS = {
  1: 'level1FaultDetectionWaitingDays',
  2: 'level2FaultDetectionWaitingDays',
  3: 'karsanSparePartSupplyWaitingDays',
  4: 'karsanSparePartShipmentWaitingDays',
  5: 'localSparePartWaitingDays',
  6: 'repairWaitingDays',
  7: 'ypPortalDefinitionWaitingDays',
  8: 'orderOpeningWaitingDays',
  9: 'customerApprovalWaitingDays',
  10: 'vehicleReleasedDays'
};

const DailyInactiveVehicleReport = () => {
  const { th, getCurrentLanguageId, getCurrentLocale } = useLanguage();
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  const { data: statusOptionsData } = useGetLookUpDropdownListQuery({
    LanguageId: getCurrentLanguageId(),
    GroupCode: 'TECHNICAL_CURRENT_STATUS'
  });

  const { data: response, isLoading, refetch } = useGetVorQuery({
    activeOnly: 1,
    pageNumber: 1,
    pageSize: 1000,
    languageId: getCurrentLanguageId()
  });

  const statusOptions = useMemo(() => {
    if (!statusOptionsData) return [];
    let options = [];
    if (Array.isArray(statusOptionsData)) {
      options = statusOptionsData;
    } else if (statusOptionsData.data && Array.isArray(statusOptionsData.data)) {
      options = statusOptionsData.data;
    } else if (statusOptionsData.items && Array.isArray(statusOptionsData.items)) {
      options = statusOptionsData.items;
    }

    return [...options].sort((a, b) => Number(a.code || a.lookupCode || 0) - Number(b.code || b.lookupCode || 0));
  }, [statusOptionsData]);

  const allRecords = useMemo(() => {
    return response?.items || [];
  }, [response?.items]);

  const openClosedOptions = useMemo(
    () => [
      { label: th('technicalSupport.columnHeaders.open'), value: '1' },
      { label: th('technicalSupport.columnHeaders.closed'), value: '0' }
    ],
    [th]
  );

  const allStatusCodes = useMemo(() => {
    return statusOptions.map(status => String(status.code || status.lookupCode));
  }, [statusOptions]);

  const statusColumns = useMemo(() => {
    const labelByCode = new Map(
      statusOptions.map((status) => [
        String(status.code || status.lookupCode || ''),
        status.translatedText || status.text || ''
      ])
    );

    return Object.entries(STATUS_DAY_FIELDS).map(([code, field]) => ({
      code,
      field,
      label: labelByCode.get(code) || th(`dailyInactiveVehicleReport.statuses.${code}`)
    }));
  }, [statusOptions, th]);

  useEffect(() => {
    if (allRecords.length === 0) {
      setChartData({ labels: [], datasets: [] });
      return;
    }

    let statusCodesToUse = allStatusCodes;
    if (statusOptions.length === 0) {
      const uniqueStatusCodes = [...new Set(allRecords.map(r => String(r.statusNotification)).filter(Boolean))];
      statusCodesToUse = uniqueStatusCodes;
    }

    const statusCounts = {};
    statusCodesToUse.forEach(code => {
      statusCounts[code] = 0;
    });

    allRecords.forEach(record => {
      const statusCode = String(record.statusNotification);
      if (statusCounts[statusCode] !== undefined) {
        statusCounts[statusCode]++;
      }
    });

    const labels = [];
    const data = [];
    const backgroundColors = [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40',
      '#FF6384',
      '#C9CBCF',
      '#4BC0C0',
      '#FF9F40'
    ];

    if (statusOptions.length === 0) {
      Object.entries(statusCounts)
        .filter(([_, count]) => count > 0)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([code, count]) => {
          labels.push(th('dailyInactiveVehicleReport.statusCode', { code }) || `Status ${code}`);
          data.push(count);
        });
    } else {
      statusOptions.forEach((status) => {
        const statusCode = String(status.code || status.lookupCode);
        const count = statusCounts[statusCode];
        if (count > 0) {
          labels.push(status.translatedText || status.text || `Status ${statusCode}`);
          data.push(count);
        }
      });
    }

    if (labels.length === 0) {
      setChartData({ labels: [], datasets: [] });
      return;
    }

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');

    setChartData({
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColors.slice(0, labels.length),
          hoverBackgroundColor: backgroundColors.slice(0, labels.length)
        }
      ]
    });

    setChartOptions({
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            color: textColor
          },
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    });
  }, [statusOptions, allRecords, allStatusCodes, th]);

  const pivotData = useMemo(() => allRecords.map((record) => {
    const row = { ...record };

    statusColumns.forEach(({ code, field }) => {
      const value = Number(record?.[field]);
      row[`status_${code}`] = Number.isFinite(value) ? value : 0;
    });

    return row;
  }), [allRecords, statusColumns]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const locale = getCurrentLocale();
    return new Date(dateString).toLocaleDateString(locale);
  };

  const dayBodyTemplate = (rowData, field) => {
    const value = rowData[field];
    return value === null || value === undefined ? '0' : String(value);
  };

  const getTagStyle = (code) => {
    const numCode = Number(code);
    if ([5, 6, 7, 8].includes(numCode)) {
      return { backgroundColor: '#FFFF00', color: '#000000', border: 'none' };
    }
    return undefined;
  };

  const openClosedBodyTemplate = (rowData) => {
    const value = rowData?.statusNotificationInf;

    if (value === null || value === undefined || value === '') {
      return <span className="text-500">-</span>;
    }

    const option = openClosedOptions.find((item) => item.value === String(value));
    return <span>{option?.label || th('common.unknown')}</span>;
  };

  const statusBodyTemplate = (rowData) => {
    const statusCode = rowData?.statusNotification;

    if (statusCode === null || statusCode === undefined || statusCode === '') {
      return <span className="text-500">-</span>;
    }

    const statusItem = statusOptions.find((item) => String(item.code || item.lookupCode) === String(statusCode));
    const statusText = statusItem?.translatedText || statusItem?.text || th('common.unknown');
    const numCode = Number(statusCode);
    let severity;
    if ([1, 2, 3, 4].includes(numCode)) severity = 'warning';
    else if (numCode === 9) severity = 'success';
    else severity = null;

    return <Tag value={statusText} severity={severity} style={getTagStyle(statusCode)} />;
  };

  const solutionDurationBodyTemplate = (rowData) => {
    if (rowData?.gunFarki === null || rowData?.gunFarki === undefined || rowData?.gunFarki === '') {
      return <span className="text-500">-</span>;
    }

    const duration = Number(rowData.gunFarki);
    if (!Number.isFinite(duration)) {
      return <span className="text-500">-</span>;
    }

    const style = duration > 7 ? { color: '#d32f2f', fontWeight: 'bold' } : undefined;

    return (
      <span style={style}>
        {duration > 7 && <span style={{ marginRight: '4px' }}>🔴</span>}
        {duration}
      </span>
    );
  };

  return (
    <div className="grid">
      <div className="col-12">
        <div className="flex justify-content-between align-items-center mb-4">
          <h2 className="m-0">{th('dailyInactiveVehicleReport.title')}</h2>
          <div className="flex gap-2">
              <GenericFileExportButton
                useExportQuery={useExportDailyInactiveVehicleToExcelMutation}
                exportParams={{ compressionType: 1 }}
                buttonLabel={th('common.export') || 'Excel'}
                buttonClassName="p-button-success"
                icon="pi pi-file-excel"
                fileNameExtractor={(result) => result?.fileName || 'daily_inactive_vehicle_report.xlsx'}
                dataExtractor={(result) => result?.fileBase64}
                contentTypeExtractor={(result) => result?.contentType}
              />
              <Button
                label={th('dailyInactiveVehicleReport.refresh')}
                icon="pi pi-refresh"
                onClick={() => refetch()}
                loading={isLoading}
                className="p-button-outlined"
              />
          </div>
        </div>
      </div>

      <div className="col-12">
        <Card title={`${th('dailyInactiveVehicleReport.statusDistribution')} (${th('dailyInactiveVehicleReport.totalRecords', { count: allRecords.length })})`}>
          {isLoading ? (
            <div className="flex justify-content-center align-items-center" style={{ height: '250px' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
            </div>
          ) : chartData.labels && chartData.labels.length > 0 ? (
            <div className="flex justify-content-center">
              <Chart type="pie" data={chartData} options={chartOptions} style={{ width: '400px', height: '400px' }} />
            </div>
          ) : (
            <div className="text-center p-4">
              <p>{th('dailyInactiveVehicleReport.noData')}</p>
            </div>
          )}
        </Card>
      </div>

      <div className="col-12">
        <Card>
          <DataTable
            value={pivotData}
            loading={isLoading}
            emptyMessage={th('dailyInactiveVehicleReport.emptyMessage')}
            stripedRows
            showGridlines
            scrollable
            scrollHeight="600px"
            className="text-sm"
          >
            <Column
              field="statusNotificationInf"
              header={th('technicalSupport.columnHeaders.openClosed')}
              body={openClosedBodyTemplate}
              frozen
              style={{ width: '120px', minWidth: '120px' }}
            />
            <Column
              field="statusNotification"
              header={th('technicalSupport.columnHeaders.status')}
              body={statusBodyTemplate}
              frozen
              style={{ width: '220px', minWidth: '220px' }}
            />
            <Column
              field="id"
              header={th('dailyInactiveVehicleReport.columns.id')}
              frozen
              style={{ width: '80px', minWidth: '80px' }}
            />
            <Column
              field="chassisNo"
              header={th('dailyInactiveVehicleReport.columns.chassisNo')}
              frozen
              style={{ width: '180px', minWidth: '180px' }}
            />
            <Column
              field="gunFarki"
              header={th('technicalSupport.columnHeaders.solutionDuration')}
              body={solutionDurationBodyTemplate}
              frozen
              style={{ width: '150px', minWidth: '150px' }}
            />
            <Column
              field="createdDate"
              header={th('technicalSupport.columnHeaders.requestCreatedDate')}
              body={(rowData) => formatDate(rowData.createdDate)}
              style={{ width: '170px', minWidth: '170px' }}
            />
            <Column
              field="solutionDate"
              header={th('technicalSupport.fields.solutionDate')}
              body={(rowData) => (rowData?.solutionDate ? formatDate(rowData.solutionDate) : '')}
              style={{ width: '150px', minWidth: '150px' }}
            />
            <Column
              field="dealerName"
              header={th('dailyInactiveVehicleReport.columns.dealer')}
              style={{ width: '200px', minWidth: '200px' }}
            />
            <Column
              field="vehicleModelEmissions"
              header={th('dailyInactiveVehicleReport.columns.vehicleModel')}
              style={{ width: '150px', minWidth: '150px' }}
            />
            <Column
              field="kmInformation"
              header={th('dailyInactiveVehicleReport.columns.km')}
              style={{ width: '100px', minWidth: '100px' }}
            />
            <Column
              field="vehicleCustomerInf"
              header={th('dailyInactiveVehicleReport.columns.customer')}
              style={{ width: '200px', minWidth: '200px' }}
            />
            <Column
              field="errorDescription"
              header={th('dailyInactiveVehicleReport.columns.errorDescription')}
              style={{ width: '250px', minWidth: '250px' }}
            />

            {statusColumns.map((status) => (
              <Column
                key={status.code}
                field={`status_${status.code}`}
                header={`${status.label} (${th('dailyInactiveVehicleReport.days')})`}
                body={(rowData) => dayBodyTemplate(rowData, `status_${status.code}`)}
                style={{ width: '160px', minWidth: '160px' }}
              />
            ))}
          </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default DailyInactiveVehicleReport;
